import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Grid,
  Paper,
  IconButton,
  Switch,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DownloadOutlined, Edit, DeleteOutline } from '@mui/icons-material';
import RightDrawer from '../../components/RightDrawer';
import { Add } from '@mui/icons-material';
import { GET_LISTBYID, SAVE, DELETE_DATA, UPDATE, GET_LIST, UPDATE2, GET_FILE } from '../../utils/api';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import { DataGrid } from '@mui/x-data-grid';
import { Alert, Snackbar } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const steps = ['Vendor Info', 'Contact Info', 'Address Info'];

const VendorFormStepper = ({ open, editId, editData, onClose, onSuccess, fetchInventory, setAlert, searchText }) => {
  const [activeStep, setActiveStep] = useState(0);
  const initialForm = {
    vendorLegalName: '',
    dbaName: '',
    website: '',
    vendorType: '',
    ownershipType: '',
    taxId: '',
    contactFirstName: '',
    contactLastName: '',
    jobTitle: '',
    contactEmail: '',
    contactPhone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    vendorStatus: '',
    notes: '',
  };
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const { logout } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (editData) {
      console.log(editData)
      setFormData({
        vendorLegalName: editData.vendor_name,
        dbaName: editData.dba_name,
        website: editData.business_website,
        vendorType: editData.vendor_type,
        ownershipType: editData.ownership_type,
        taxId: editData.tax_id,
        contactFirstName: editData.contact_name,
        contactLastName: editData.contact_last_name,
        jobTitle: editData.job_title,
        contactEmail: editData.contact_email,
        contactPhone: editData.contact_phone,
        address1: editData.address_line1,
        address2: editData.address_line2,
        city: editData.city,
        state: editData.state,
        zip: editData.zip_code,
        country: editData.country,
        vendorStatus: editData.active ? "active" : "inactive",
        notes: editData.additional_notes,
      });
    } else {
      setFormData(initialForm);
    }
  }, [editData]);

  const requiredFields = [
    'vendorLegalName',
    'vendorType',
    'ownershipType',
    'taxId',
    'contactEmail',
    'contactPhone',
    'zip',
    'country',
    'vendorStatus',
  ];

  const validate = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        newErrors[field] = 'This field is required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (e) =>
    setFormData({ ...formData, [field]: e.target.value });

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleClose = () => {
    setActiveStep(0);
    setFormData({});
    onClose();
  };


  const handleSaveVendor = async () => {
    if (!validate()) return;

    const payload = {
      vendor_name: formData.vendorLegalName,
      dba_name: formData.dbaName,
      business_website: formData.website,
      vendor_type: formData.vendorType,
      ownership_type: formData.ownershipType,
      tax_id: formData.taxId,
      contact_name: formData.contactFirstName,
      contact_email: formData.contactEmail,
      contact_phone: formData.contactPhone,
      zip_code: formData.zip,
      country: formData.country,
      active: formData.vendorStatus == 'active' ? true : false,
      additional_notes: formData.notes,
    };

    try {
      let res;
      if (editId) {
        res = await UPDATE(`/vendors/${editId}`, payload);
      } else {
        res = await SAVE('/vendors', payload);
      }

      if (res.statusCode == 401) {
        logout();
        navigate('/login');
      }
      if (res.status === 200 || res.status === 201) {
        setAlert({
          type: 'success',
          message: editId
            ? 'Vendor updated successfully!'
            : 'Vendor added successfully!',
          open: true,
        });
        await fetchInventory(searchText);
        onSuccess();
      } else {
        setAlert({
          type: 'error',
          message:
            res?.data?.message ||
            `Failed with status ${res.status}`,
          open: true,
        });
      }
    } catch (error) {
      console.error('Error saving vendor:', error);
      setAlert({
        type: 'error',
        message: 'Something went wrong while saving the vendor.',
        open: true,
      });
    } finally {
      // reset form & exit edit mode
      setFormData({
        vendorLegalName: '',
        dbaName: '',
        website: '',
        vendorType: '',
        ownershipType: '',
        taxId: '',
        contactFirstName: '',
        contactLastName: '',
        jobTitle: '',
        contactEmail: '',
        contactPhone: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        vendorStatus: '',
        notes: '',
      });
      onClose();          // close drawer
    }
  };


  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack sx={{ textAlign: "center" }} spacing={2}>
            <Typography variant="h6">Vendor Info</Typography>
            <TextField
              required
              size="small"
              label="Vendor Legal Name"
              value={formData.vendorLegalName}
              onChange={handleChange('vendorLegalName')}
            />
            <TextField
              size="small"
              label="DBA Name (If applicable)"
              value={formData.dbaName}
              onChange={handleChange('dbaName')}
            />
            <TextField
              size="small"
              label="Business Website"
              required
              value={formData.website}
              onChange={handleChange('website')}
            />
            <FormControl required size="small" fullWidth>
              <InputLabel>Vendor Type</InputLabel>
              <Select
                value={formData.vendorType}
                onChange={handleChange('vendorType')}
                label="Vendor Type"
              >
                <MenuItem value="manufacturer">Manufacturer</MenuItem>
                <MenuItem value="distributor">Distributor</MenuItem>
                <MenuItem value="retailer">Retailer</MenuItem>
              </Select>
            </FormControl>
            <FormControl required size="small" fullWidth>
              <InputLabel>Ownership Type</InputLabel>
              <Select
                value={formData.ownershipType}
                onChange={handleChange('ownershipType')}
                label="Ownership Type"
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="government">Government</MenuItem>
              </Select>
            </FormControl>
            <TextField
              required
              size="small"
              label="Tax ID/VAT Number"
              value={formData.taxId}
              onChange={handleChange('taxId')}
            />
          </Stack>
        );

      case 1:
        return (
          <Stack sx={{ textAlign: "center" }} spacing={2}>
            <Typography variant="h6">Contact Info</Typography>
            <TextField
              size="small"
              label="Contact Name"
              value={formData.contactFirstName}
              onChange={handleChange('contactFirstName')}
            />
            <TextField
              required
              size="small"
              label="Contact Email"
              value={formData.contactEmail}
              onChange={handleChange('contactEmail')}
            />
            <TextField
              required
              size="small"
              label="Contact Phone"
              value={formData.contactPhone}
              onChange={handleChange('contactPhone')}
            />
          </Stack>
        );

      case 2:
        return (
          <Stack sx={{ textAlign: "center" }} spacing={2}>
            <Typography variant="h6">Address Info</Typography>
            <TextField
              required
              size="small"
              label="Zip/Postal Code"
              value={formData.zip}
              onChange={handleChange('zip')}
            />
            <TextField
              required
              size="small"
              label="Country"
              value={formData.country}
              onChange={handleChange('country')}
            />
            <FormControl required size="small" fullWidth>
              <InputLabel>Vendor Status</InputLabel>
              <Select
                value={formData.vendorStatus}
                onChange={handleChange('vendorStatus')}
                label="Vendor Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <TextField
              multiline
              rows={1}
              size="small"
              label="Additional Notes/Comments"
              value={formData.notes}
              onChange={handleChange('notes')}
            />
          </Stack>
        );

      default:
        return null;
    }
  };

  const renderStepDots = (
    <Box display="flex" justifyContent="center" gap={1} mb={2}>
      {steps.map((_, index) => (
        <Box
          key={index}
          width={12}
          height={12}
          borderRadius="50%"
          border="2px solid #2c3e55"
          bgcolor={index === activeStep ? '#2c3e55' : 'transparent'}
        />
      ))}
    </Box>
  );



  const renderFooterActions = () => {
    const buttonStyle = {
    };

    if (activeStep === 0) {
      return (
        <Button sx={buttonStyle} onClick={handleNext}>
          Next
        </Button>
      );
    }

    if (activeStep === 1) {
      return (
        <>
          <Button onClick={handleBack} sx={buttonStyle}>
            Back
          </Button>
          <Button sx={buttonStyle} onClick={handleNext}>
            Next
          </Button>
        </>
      );
    }

    if (activeStep === 2) {
      return (
        <>
          <Button onClick={handleBack} sx={buttonStyle}>
            Back
          </Button>
        </>
      );
    }
  };

  return (
    <RightDrawer onSave={handleSaveVendor} open={open} onClose={handleClose} title={editId ? "Edit Vendor" : "Add New Vendor"}>
      {renderStepDots}
      <Box>{renderStepContent()}</Box>
      <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
        {renderFooterActions()}
      </Box>
    </RightDrawer>
  );
};

const VendorMaster = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vendorRows, setVendorRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', open: false });
  const [editId, setEditId] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const columns = [
    { field: 'vendor_name', headerName: 'Name', flex: 1 },
    { field: 'vendor_id', headerName: 'ID', flex: 1 },
    { field: 'contact_email', headerName: 'Email', flex: 1 },
    { field: 'contact_phone', headerName: 'Phone', flex: 1 },
    { field: 'vendor_type', headerName: 'Vendor Type', flex: 1 },
    { field: 'location', headerName: 'Location', flex: 1 },
    {
      field: 'active',
      headerName: 'Status',
      sortable: false,
      flex: 1,
      headerAlign: 'center',
      renderCell: (params) => (
        <>
          <Switch
            checked={params.value}
            onChange={async () => {
              try {
                { console.log(params.id) }
                console.log(params)
                const response = await UPDATE2(`/vendors/status/${params.id}?active=${!params.value}`);

                if (response?.status === 200 || response?.status === 201) {
                  setAlert({
                    type: 'success',
                    message: `Vendor ${!params.value ? 'Activated' : 'Deactivated'} successfully!`,
                    open: true,
                  });
                  fetchInventory(searchText);
                } else {
                  setAlert({
                    type: 'error',
                    message: 'Failed to update user status.',
                    open: true,
                  });
                }
              } catch (error) {
                console.error('Error updating user status:', error);
                setAlert({
                  type: 'error',
                  message: 'Failed to update user status.',
                  open: true,
                });
              }
            }}
            color="success"
          />
          {params.value ? 'Active' : 'Inactive'}
        </>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditClick(params.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>


          <Tooltip title="Delete">
            <IconButton
              size="small"
              sx={{ color: 'red' }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(params.id);
              }}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const handleEditClick = async (id) => {
    try {
      const resp = await GET_LISTBYID('/vendors', id);
      setEditId(id);
      setInitialData(resp);
      setDrawerOpen(true);
    } catch (e) {
      console.error(e);
      setAlert({ open: true, type: 'error', message: 'Failed to load for edit' });
    }
  };

  const fetchInventory = async (searchText = '') => {
    setLoading(true);
    try {
      let queryParams = {
        ...(searchText ? { searchText } : {}),
        limit: limit
      };

      const data = await GET_LIST('/vendors', queryParams);
      if (data.statusCode == 401) {
        logout();
        navigate('/login');
      }
      setVendorRows(data.data);
      setTotalPages(data.data.totalPages)
      // setRows(data?.data || []); // adjust if your API structure is different
      // setError(null);
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
      // setError('Error loading inventory');
    } finally {
      setLoading(false);
    }
  };

  // Open delete confirmation
  const handleOpenDeleteDialog = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  // Close without deleting
  const handleCloseDeleteDialog = () => {
    setSelectedId(null);
    setDeleteDialogOpen(false);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    try {
      await DELETE_DATA('/vendors', selectedId);
      setAlert({
        type: 'success',
        message: 'Vendor deleted successfully.',
        open: true,
      });
      await fetchInventory(searchText);
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'Failed to delete the vendor.',
        open: true,
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleDownload = async () => {
    try {
      const queryParams = {
        ids: selected.join(','),
      };
      await GET_FILE('/vendors/download/excel', queryParams, 'xlsx', 'vendors');
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };


  useEffect(() => {
    fetchInventory(searchText);
  }, []);

  return (
    <Box>
      <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
        Vendor Master
      </Typography>
      <Typography variant="body2" sx={{ color: 'gray', mb: 1 }}>
        Last Updated: {new Date().toLocaleDateString()}
      </Typography>

      <Grid
        container
        spacing={0.5}
        sx={{ mb: 1 }}
        alignItems="center"
        justifyContent="flex-end"
        wrap="nowrap"
      >
        {/* Search Field */}
        <Grid item>
          <TextField
            id="outlined-size-small"
            label="Search"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
                fetchInventory(searchText);
              }
            }}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'gray', mr: 1 }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />
        </Grid>
        
        {selected.length > 0 && (
          <Grid item>
            <Button variant="contained" startIcon={<DownloadOutlined />} size="small" sx={{ p: 0.5 }} color="primary" onClick={handleDownload}>
              Download
            </Button>
          </Grid>
        )}

        <Grid item>
          <Button
            onClick={() => setDrawerOpen(true)}
            size="small"
            variant='contained'
            startIcon={<Add />}
          >
            Add Vendor
          </Button>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%', mb: 2, borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box
            sx={{
              height: 'calc(100vh - 190px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={30} />
          </Box>
        ) : (
          <>
            <Box sx={{ height: 'calc(100vh - 210px)', width: '100%' }}>
              <DataGrid
                rows={vendorRows}
                columns={columns}
                checkboxSelection
                hideFooterPagination
                disableRowSelectionOnClick
                onRowSelectionModelChange={(selectionModel) => {
                  const idsArray = Array.from(selectionModel.ids || []);
                  setSelected(idsArray);
                }}
                selectionModel={selected}
                sortingOrder={['asc', 'desc']}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0
                    ? 'even-row'
                    : 'odd-row'
                }
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f2f2f2',
                  },
                  '& .even-row': {
                    backgroundColor: '#f6f6f6',
                  },
                  '& .odd-row': {
                    backgroundColor: '#ffffff',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    minHeight: '0px'
                  },
                  border: 'none',
                }}
              />
            </Box>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px" }}>
              <Pagination
                count={totalPages}
                page={page}
                color="primary"
                onChange={(e, value) => setPage(value)}
                shape="rounded"
              />

            </div>
          </>
        )}
      </Paper>

      <VendorFormStepper
        open={drawerOpen}
        editId={editId}
        editData={initialData}
        onClose={() => {
          setDrawerOpen(false);
          setEditId(null);
          setInitialData(null);
        }}
        onSuccess={() => {
          setDrawerOpen(false);
          setEditId(null);
          setInitialData(null);
          fetchInventory(searchText);
        }}
        fetchInventory={fetchInventory}
        setAlert={setAlert}
        searchText={searchText}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this vendor?"
      />

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={alert.type}
          variant="filled"
          onClose={() => setAlert({ ...alert, open: false })}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default VendorMaster;
